import { create } from 'zustand/react';
import { DEMO_TENANT_ID } from '../data/tenantDefaults';

// Team performance intelligence (AEGIS-12) — activity tracking on, weekly
// coaching digest on, anomaly alerts on, benchmark view anonymous. Tenant
// admins configure what data is collected and who can see profiles in
// SystemSettings.
const DEFAULT_PERFORMANCE_SETTINGS = {
  activityTrackingEnabled: true,       // collect per-user daily metrics
  weeklyCoachingEnabled: true,          // generate weekly AEGIS coaching notes
  anomalyAlertsEnabled: true,           // alert manager on personal regression
  recognitionTriggersEnabled: true,     // flag personal bests / exceptional work
  benchmarkMode: 'anonymous_percentile',// anonymous_percentile | team_named | off
  profileVisibility: 'manager_and_self',// manager_and_self | admin_only | self_only
  dataRetentionDays: 365,
  regressionThresholdPct: 0.15,         // flag when metric drops ≥15% vs baseline
  burnoutSignalEnabled: true,           // compound throughput/error/response regression
  trackedMetrics: {
    dispatcher: ['loadsHandled', 'tenderAcceptanceRate', 'carrierDiversity', 'exceptionResolutionTime', 'costPerLoadDelta'],
    picker:     ['picksPerHour', 'accuracyRate', 'zoneEfficiency', 'shortPickRate'],
    cxRep:      ['avgResponseTimeMin', 'slaBreachRate', 'customerNps', 'escalationRate'],
  },
};

// Risk intelligence (AEGIS-11) — ingest on, conservative thresholds, all
// severities visible. Tenants tune in SystemSettings.
const DEFAULT_RISK_SETTINGS = {
  ingestEnabled: true,
  minCostImpactUsd: 5000,       // below this, an event is informational-only
  watchedCountries: [],          // ISO codes; empty == all
  watchedCommodities: [],        // HTS / free-text keywords
  includeTariffAlerts: true,
  includePortDisruption: true,
  includeGeopolitical: true,
  notifyChannels: { inApp: true, email: false },
  alertSeverityFloor: 'medium', // minimum severity surfaced as an alert
};

// Voice settings default to AEGIS-enabled with conservative routing — AEGIS
// handles routine check calls autonomously and escalates anything that
// smells like trouble (frustrated sentiment, repeated failures) to a human.
const DEFAULT_VOICE_SETTINGS = {
  aegisEnabled: true,
  persona: {
    name: 'Kira',
    voice: 'warm_female',
    tone: 'professional',
    neverRevealAI: true,     // spec: never reveals it's AI unless asked directly
  },
  languages: {
    primary: 'en-US',
    secondary: [],
  },
  routing: {
    check_call: 'aegis',
    delivery_confirmation: 'aegis',
    exception_report: 'aegis',
    customer_status: 'aegis',
    pickup_confirmation: 'aegis',
    delivery_notification: 'aegis',
    delay_notification: 'aegis',
    other: 'human',
  },
  escalation: {
    sentimentThreshold: 'frustrated',
    maxFailedAttempts: 3,
    includeTranscriptSummary: true,
  },
  outboundWindow: {
    pickupConfirmationHoursBefore: 2,
    deliveryNotificationHoursBefore: 1,
  },
};

// AEGIS voice (operator-facing conversational voice — AEGIS-13). Separate
// from voiceSettings above (AEGIS-9, which handles outbound carrier/customer
// calls via Bland.ai). Persona lives here, so does action authorization.
const DEFAULT_AEGIS_VOICE_SETTINGS = {
  enabled: true,
  persona: {
    voiceName: 'AEGIS',                // label + speech-voice name match hint
    verbosity: 'standard',             // concise | standard | detailed
    lang: 'en-US',
    rate: 1.0,
    pitch: 1.0,
    engine: 'browser',                 // browser | elevenlabs
  },
  wakeWordEnabled: false,              // off by default — prevents accidents
  wakeWord: 'hey aegis',
  actionAuthorization: {
    tender_load: 'confirm',
    place_hold: 'confirm',
    send_notification: 'confirm',
    create_exception: 'confirm',
    escalate_to_human: 'confirm',
    draft_communication: 'auto',       // drafts don't send anything externally
    update_record: 'confirm',
    query_data: 'auto',                // read-only always auto
  },
  confidenceThreshold: 0.8,            // below this, always confirm
  maxActionsPerSession: 10,
  retainTranscriptOnServer: false,     // privacy by design
};

// Quote settings default to approval OFF — tenants opt in via SystemSettings.
const DEFAULT_QUOTE_SETTINGS = {
  requireApproval: false,
  approvalThreshold: 10,        // margin % below which approval is required
  approvalRequiredBelow: null,  // $ floor below which approval is required
  approvers: [],                // userIds allowed to approve
  autoExpireDays: 7,            // default quote validity
  autoExpire: true,             // enable auto-expiry job
  showMarginToSalespeople: true,
  defaultCurrency: 'USD',
};

const DEFAULT_TENANT_CONFIG = {
  timezone: 'America/Chicago',
  dateFormat: 'MM/DD/YYYY',
  currency: 'USD',
  weightUnit: 'lbs',
  distanceUnit: 'miles',
  modules: ['overview', 'tms', 'wms', 'ims', 'cx', 'crm', 'iot', 'fleet', 'aegis', 'system'],
  features: {
    autoTender: true,
    aegisIntelligence: true,
    customerPortal: true,
    iotMonitoring: true,
    fleetManagement: true,
    voiceCalls: true,
  },
  quoteSettings: DEFAULT_QUOTE_SETTINGS,
  voiceSettings: DEFAULT_VOICE_SETTINGS,
  aegisVoiceSettings: DEFAULT_AEGIS_VOICE_SETTINGS,
  riskSettings: DEFAULT_RISK_SETTINGS,
  performanceSettings: DEFAULT_PERFORMANCE_SETTINGS,
};

const INITIAL_STATE = {
  currentModule: 'overview',
  currentUser: null,
  // Default tenantId kept non-null so pre-auth/demo DataService queries have a
  // predictable tenant scope. Overwritten by useAuth after login.
  tenantId: DEMO_TENANT_ID,
  currentTenant: null,
  tenantConfig: DEFAULT_TENANT_CONFIG,
  isSuperAdmin: false,
  sidebarExpanded: true,
  notifications: 0,
  aegisAlerts: 0,
  // CRUISE-NCL-FLEET-SHIP-CONTEXT-1: per-operator ship context selection.
  // Null = fleet-aggregate view (no single ship selected).
  nclShipId:           null,
  nclBrandId:          'ncl-brand',
  nclCurrentSailingId: null,
};

const useAppStore = create((set, get) => ({
  ...INITIAL_STATE,

  setCurrentModule: (mod) => set({ currentModule: mod }),
  setCurrentUser: (user) => set({ currentUser: user, isSuperAdmin: !!user?.isSuperAdmin }),
  setTenantId: (id) => {
    import("../platform/hasCapability").then(m => m.clearCapabilityCache());
    import("../services/CapabilityService").then(m => m.clearCapabilityServiceCache());
    return set({ tenantId: id || DEMO_TENANT_ID });
  },

  // Attach full tenant record + config after login.
  setTenantContext: (tenant) => set({
    currentTenant: tenant || null,
    tenantConfig: tenant?.config ? {
      ...DEFAULT_TENANT_CONFIG,
      ...tenant.config,
      features: { ...DEFAULT_TENANT_CONFIG.features, ...(tenant.config.features || {}) },
      quoteSettings: { ...DEFAULT_QUOTE_SETTINGS, ...(tenant.config.quoteSettings || {}) },
      voiceSettings: {
        ...DEFAULT_VOICE_SETTINGS,
        ...(tenant.config.voiceSettings || {}),
        persona: { ...DEFAULT_VOICE_SETTINGS.persona, ...((tenant.config.voiceSettings || {}).persona || {}) },
        routing: { ...DEFAULT_VOICE_SETTINGS.routing, ...((tenant.config.voiceSettings || {}).routing || {}) },
        escalation: { ...DEFAULT_VOICE_SETTINGS.escalation, ...((tenant.config.voiceSettings || {}).escalation || {}) },
        languages: { ...DEFAULT_VOICE_SETTINGS.languages, ...((tenant.config.voiceSettings || {}).languages || {}) },
        outboundWindow: { ...DEFAULT_VOICE_SETTINGS.outboundWindow, ...((tenant.config.voiceSettings || {}).outboundWindow || {}) },
      },
      riskSettings: {
        ...DEFAULT_RISK_SETTINGS,
        ...(tenant.config.riskSettings || {}),
        notifyChannels: { ...DEFAULT_RISK_SETTINGS.notifyChannels, ...((tenant.config.riskSettings || {}).notifyChannels || {}) },
      },
      performanceSettings: {
        ...DEFAULT_PERFORMANCE_SETTINGS,
        ...(tenant.config.performanceSettings || {}),
        trackedMetrics: {
          ...DEFAULT_PERFORMANCE_SETTINGS.trackedMetrics,
          ...((tenant.config.performanceSettings || {}).trackedMetrics || {}),
        },
      },
      aegisVoiceSettings: {
        ...DEFAULT_AEGIS_VOICE_SETTINGS,
        ...(tenant.config.aegisVoiceSettings || {}),
        persona: { ...DEFAULT_AEGIS_VOICE_SETTINGS.persona, ...((tenant.config.aegisVoiceSettings || {}).persona || {}) },
        actionAuthorization: { ...DEFAULT_AEGIS_VOICE_SETTINGS.actionAuthorization, ...((tenant.config.aegisVoiceSettings || {}).actionAuthorization || {}) },
      },
    } : DEFAULT_TENANT_CONFIG,
    tenantId: tenant?.tenantId || get().tenantId,
  }),

  // In-memory quote settings update — persisted via DataService.upsertTenant
  // by the SystemSettings screen.
  updateQuoteSettings: (patch) => set((s) => ({
    tenantConfig: {
      ...s.tenantConfig,
      quoteSettings: { ...DEFAULT_QUOTE_SETTINGS, ...(s.tenantConfig?.quoteSettings || {}), ...(patch || {}) },
    },
  })),

  // Team performance settings — deep-merge (same pattern as voice/quote).
  updatePerformanceSettings: (patch) => set((s) => {
    const prev = s.tenantConfig?.performanceSettings || DEFAULT_PERFORMANCE_SETTINGS;
    return {
      tenantConfig: {
        ...s.tenantConfig,
        performanceSettings: {
          ...prev,
          ...(patch || {}),
          trackedMetrics: { ...prev.trackedMetrics, ...(patch?.trackedMetrics || {}) },
        },
      },
    };
  }),

  // Risk intelligence settings — deep-merge (same pattern as voice/quote).
  updateRiskSettings: (patch) => set((s) => {
    const prev = s.tenantConfig?.riskSettings || DEFAULT_RISK_SETTINGS;
    return {
      tenantConfig: {
        ...s.tenantConfig,
        riskSettings: {
          ...prev,
          ...(patch || {}),
          notifyChannels: { ...prev.notifyChannels, ...(patch?.notifyChannels || {}) },
        },
      },
    };
  }),

  // AEGIS-13 voice settings (operator-facing) — deep-merge persona + auth map.
  updateAegisVoiceSettings: (patch) => set((s) => {
    const prev = s.tenantConfig?.aegisVoiceSettings || DEFAULT_AEGIS_VOICE_SETTINGS;
    return {
      tenantConfig: {
        ...s.tenantConfig,
        aegisVoiceSettings: {
          ...prev,
          ...(patch || {}),
          persona: { ...prev.persona, ...(patch?.persona || {}) },
          actionAuthorization: { ...prev.actionAuthorization, ...(patch?.actionAuthorization || {}) },
        },
      },
    };
  }),

  // Voice settings — deep-merge nested config blocks so partial patches work.
  updateVoiceSettings: (patch) => set((s) => {
    const prev = s.tenantConfig?.voiceSettings || DEFAULT_VOICE_SETTINGS;
    return {
      tenantConfig: {
        ...s.tenantConfig,
        voiceSettings: {
          ...prev,
          ...(patch || {}),
          persona: { ...prev.persona, ...(patch?.persona || {}) },
          routing: { ...prev.routing, ...(patch?.routing || {}) },
          escalation: { ...prev.escalation, ...(patch?.escalation || {}) },
          languages: { ...prev.languages, ...(patch?.languages || {}) },
          outboundWindow: { ...prev.outboundWindow, ...(patch?.outboundWindow || {}) },
        },
      },
    };
  }),

  // Feature flag helper — returns false for unknown features.
  hasFeature: (featureName) => {
    const features = get().tenantConfig?.features;
    return !!(features && features[featureName]);
  },

  // Module access helper — modules listed in tenantConfig.modules are visible.
  hasModule: (moduleName) => {
    const modules = get().tenantConfig?.modules;
    if (!Array.isArray(modules)) return true; // permissive default
    return modules.includes(moduleName);
  },

  // CRUISE-NCL-FLEET-SHIP-CONTEXT-1: ship context selection (per operator session).
  setNclShipContext: ({ shipId, brandId, currentSailingId }) => set({
    nclShipId:           shipId           ?? null,
    nclBrandId:          brandId          ?? 'ncl-brand',
    nclCurrentSailingId: currentSailingId ?? null,
  }),
  setNclBrandContext: (brandId) => set({ nclBrandId: brandId ?? 'ncl-brand' }),

  // Clears all user-bound state on signOut.
  resetState: () => set({ ...INITIAL_STATE }),
}));

export default useAppStore;
// Named alias for WIRING-SWEEP-CAP-1 import pattern
export const useStore = useAppStore;
